import { useState, useEffect, useRef } from "react";
import { CheckCircleFilled, LeftOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";

const DIGIT_WORDS = { 0:"ZERO",1:"ONE",2:"TWO",3:"THREE",4:"FOUR",5:"FIVE",6:"SIX",7:"SEVEN",8:"EIGHT",9:"NINE" };
function sanitizeId(id) {
  return (id || "SCREEN")
    .replace(/\d/g, (d) => `_${DIGIT_WORDS[d]}`)
    .replace(/[^A-Za-z_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/, "")
    .toUpperCase();
}

const WA = {
  green:    "#25D366",
  teal:     "#128C7E",
  tealDark: "#075E54",
  bg:       "#ECE5DD",
  white:    "#FFFFFF",
  border:   "#E5E5EA",
  inputBg:  "#F2F2F7",
  label:    "#8696A0",
  text:     "#111B21",
  subtext:  "#667781",
  accent:   "#008069",
  link:     "#53BDEB",
  red:      "#EA4335",
};

function Comp({ comp, onAction }) {
  const p = comp.props || {};
  const [radio, setRadio] = useState("");
  const [checked, setChecked] = useState([]);

  const fieldWrap = { marginBottom: 18 };

  const labelEl = (text, required) => (
    <div style={{ fontSize: 11.5, color: WA.label, marginBottom: 5, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase" }}>
      {text}{required && <span style={{ color: WA.red, marginLeft: 2 }}>*</span>}
    </div>
  );

  const inputBase = {
    width: "100%", padding: "11px 14px", borderRadius: 10,
    border: `1.5px solid ${WA.border}`, background: WA.inputBg,
    fontSize: 14, color: WA.text, boxSizing: "border-box",
    outline: "none", fontFamily: "inherit",
  };

  switch (comp.type) {
    case "TextHeading":
      return (
        <div style={{ fontSize: 22, fontWeight: 700, color: WA.text, marginBottom: 6, lineHeight: 1.25, letterSpacing: -0.4 }}>
          {p.text || <span style={{ color: WA.label, fontStyle: "italic", fontWeight: 400, fontSize: 14 }}>Heading text</span>}
        </div>
      );
    case "TextSubheading":
      return (
        <div style={{ fontSize: 16, fontWeight: 600, color: WA.text, marginBottom: 5, lineHeight: 1.35 }}>
          {p.text || <span style={{ color: WA.label, fontStyle: "italic", fontWeight: 400, fontSize: 13 }}>Subheading</span>}
        </div>
      );
    case "TextBody":
      return (
        <div style={{
          fontSize: 14, color: WA.subtext, marginBottom: 10, lineHeight: 1.6,
          fontWeight: ["bold","bold_italic"].includes(p["font-weight"]) ? 600 : 400,
          fontStyle: ["italic","bold_italic"].includes(p["font-weight"]) ? "italic" : "normal",
        }}>
          {p.text || <span style={{ color: WA.border, fontStyle: "italic" }}>Body text</span>}
        </div>
      );
    case "RichText":
      return <div style={{ fontSize: 14, color: WA.subtext, marginBottom: 10, lineHeight: 1.6 }}>{p.text}</div>;
    case "Image":
      return (
        <div style={{ marginBottom: 16, borderRadius: 12, overflow: "hidden", background: WA.inputBg }}>
          {p.src
            ? <img src={p.src} alt={p["alt-text"] || ""} style={{ width: "100%", maxHeight: 140, objectFit: "cover", display: "block" }} />
            : <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: WA.label, fontSize: 24 }}>🖼</div>}
        </div>
      );
    case "TextInput":
      return (
        <div style={fieldWrap}>
          {labelEl(p.label || p.name, p.required)}
          <input readOnly placeholder={p["helper-text"] || `Enter ${p.label || "value"}`} style={inputBase} />
        </div>
      );
    case "TextArea":
      return (
        <div style={fieldWrap}>
          {labelEl(p.label || p.name, p.required)}
          <textarea readOnly placeholder={`Enter ${p.label || "text"}`} rows={3} style={{ ...inputBase, resize: "none" }} />
        </div>
      );
    case "DatePicker":
      return (
        <div style={fieldWrap}>
          {labelEl(p.label || p.name, p.required)}
          <div style={{ ...inputBase, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}>
            <span style={{ color: WA.label, fontSize: 14 }}>DD / MM / YYYY</span>
            <span style={{ fontSize: 16 }}>📅</span>
          </div>
        </div>
      );
    case "Dropdown": {
      const opts = p["data-source"] || [];
      return (
        <div style={fieldWrap}>
          {labelEl(p.label || p.name, p.required)}
          <div style={{ ...inputBase, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}>
            <span style={{ color: WA.label, fontSize: 14 }}>Select {p.label || "option"}</span>
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1l5 5 5-5" stroke={WA.label} strokeWidth="1.8" strokeLinecap="round"/></svg>
          </div>
          {opts.length > 0 && (
            <div style={{ marginTop: 4, border: `1.5px solid ${WA.border}`, borderRadius: 10, overflow: "hidden", background: WA.white, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              {opts.slice(0, 4).map((o, i) => (
                <div key={i} style={{ padding: "10px 14px", fontSize: 13, color: WA.text, borderBottom: i < Math.min(opts.length, 4) - 1 ? `1px solid ${WA.border}` : "none" }}>
                  {o.title || o}
                </div>
              ))}
              {opts.length > 4 && <div style={{ padding: "8px 14px", fontSize: 11, color: WA.label }}>+{opts.length - 4} more…</div>}
            </div>
          )}
        </div>
      );
    }
    case "RadioButtonsGroup": {
      const opts = p["data-source"] || [];
      return (
        <div style={fieldWrap}>
          {labelEl(p.label || p.name, p.required)}
          <div style={{ border: `1.5px solid ${WA.border}`, borderRadius: 12, overflow: "hidden", background: WA.white }}>
            {(opts.length ? opts : [{ id: "1", title: "Option A" }, { id: "2", title: "Option B" }]).map((o, i, arr) => {
              const key = o.id || String(i);
              const sel = radio === key;
              return (
                <div key={i} onClick={() => setRadio(key)} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer",
                  borderBottom: i < arr.length - 1 ? `1px solid ${WA.border}` : "none",
                  background: sel ? "#E8F5E9" : WA.white, transition: "background 0.12s",
                }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                    border: `2px solid ${sel ? WA.accent : "#C5CDD2"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {sel && <div style={{ width: 10, height: 10, borderRadius: "50%", background: WA.accent }} />}
                  </div>
                  <span style={{ fontSize: 14, color: WA.text }}>{o.title || o}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    case "CheckboxGroup": {
      const opts = p["data-source"] || [];
      const toggle = (key) => setChecked((prev) => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
      return (
        <div style={fieldWrap}>
          {labelEl(p.label || p.name, p.required)}
          <div style={{ border: `1.5px solid ${WA.border}`, borderRadius: 12, overflow: "hidden", background: WA.white }}>
            {(opts.length ? opts : [{ id: "1", title: "Option A" }, { id: "2", title: "Option B" }]).map((o, i, arr) => {
              const key = o.id || String(i);
              const sel = checked.includes(key);
              return (
                <div key={i} onClick={() => toggle(key)} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", cursor: "pointer",
                  borderBottom: i < arr.length - 1 ? `1px solid ${WA.border}` : "none",
                  background: sel ? "#E8F5E9" : WA.white,
                }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                    border: `2px solid ${sel ? WA.accent : "#C5CDD2"}`,
                    background: sel ? WA.accent : WA.white,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {sel && <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4l3 3 6-7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <span style={{ fontSize: 14, color: WA.text }}>{o.title || o}</span>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    case "ChipsSelector": {
      const opts = p["data-source"] || [];
      const toggle = (key) => setChecked((prev) => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
      return (
        <div style={fieldWrap}>
          {labelEl(p.label || p.name, p.required)}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {(opts.length ? opts : [{ id: "1", title: "Chip A" }, { id: "2", title: "Chip B" }]).map((o, i) => {
              const key = o.id || String(i);
              const sel = checked.includes(key);
              return (
                <div key={i} onClick={() => toggle(key)} style={{
                  padding: "7px 16px", borderRadius: 20, fontSize: 13, cursor: "pointer",
                  background: sel ? WA.accent : WA.white,
                  color: sel ? WA.white : WA.text,
                  border: `1.5px solid ${sel ? WA.accent : WA.border}`,
                  transition: "all 0.12s",
                }}>
                  {o.title || o}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    case "OptIn":
      return (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16, cursor: "pointer" }}>
          <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid #C5CDD2`, flexShrink: 0, marginTop: 1, background: WA.inputBg }} />
          <span style={{ fontSize: 13, color: WA.subtext, lineHeight: 1.55 }}>{p.label || "I agree to the terms"}</span>
        </div>
      );
    case "EmbeddedLink":
      return (
        <div style={{ marginBottom: 12 }}>
          <span style={{ color: WA.link, fontSize: 14, fontWeight: 500, textDecoration: "underline", cursor: "pointer" }}>
            {p.text || "Learn more"}
          </span>
        </div>
      );
    case "Footer": {
      const action = p["on-click-action"];
      return (
        <div style={{ padding: "10px 16px 16px", borderTop: `1px solid ${WA.border}`, background: WA.white }}>
          <button
            onClick={() => onAction && onAction(action)}
            style={{
              width: "100%", padding: "14px", borderRadius: 12, border: "none",
              background: `linear-gradient(135deg, ${WA.teal} 0%, ${WA.green} 100%)`,
              color: WA.white, fontSize: 15, fontWeight: 700, cursor: "pointer",
              letterSpacing: 0.3, boxShadow: "0 3px 12px rgba(18,140,126,0.35)",
            }}
          >
            {p.label || "Submit"}
          </button>
        </div>
      );
    }
    default:
      return null;
  }
}

/* ─── Phone ──────────────────────────────────────────────── */
function Phone({ flow, screens, screenIdx, setScreenIdx, history, setHistory, submitted, setSubmitted }) {
  const screen = screens[screenIdx];
  const bodyComps = (screen?.components || []).filter(c => c.type !== "Footer");
  const footerComp = (screen?.components || []).find(c => c.type === "Footer");
  const canGoBack = history.length > 0;

  const handleAction = (action) => {
    if (!action) return;
    if (action.name === "navigate" && action.next?.name) {
      const target = screens.findIndex(s =>
        sanitizeId(s.id) === action.next.name || s.id === action.next.name
      );
      if (target !== -1) {
        setHistory(h => [...h, screenIdx]);
        setScreenIdx(target);
      } else {
        setSubmitted(true);
      }
    } else {
      setSubmitted(true);
    }
  };

  const handleBack = () => {
    if (submitted) { setSubmitted(false); return; }
    if (history.length > 0) {
      setScreenIdx(history[history.length - 1]);
      setHistory(h => h.slice(0, -1));
    }
  };

  return (
    <div style={{
      width: 260,
      height: 530,
      background: "#1A1A1C",
      borderRadius: 46,
      padding: "10px 5px",
      boxShadow: "0 40px 100px rgba(0,0,0,0.55), 0 0 0 1px #383838, inset 0 1px 0 rgba(255,255,255,0.08)",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      flexShrink: 0,
    }}>
      {/* Side buttons */}
      <div style={{ position: "absolute", left: -3, top: 108, width: 3, height: 28, background: "#3a3a3c", borderRadius: "3px 0 0 3px" }} />
      <div style={{ position: "absolute", left: -3, top: 148, width: 3, height: 50, background: "#3a3a3c", borderRadius: "3px 0 0 3px" }} />
      <div style={{ position: "absolute", left: -3, top: 210, width: 3, height: 50, background: "#3a3a3c", borderRadius: "3px 0 0 3px" }} />
      <div style={{ position: "absolute", right: -3, top: 168, width: 3, height: 68, background: "#3a3a3c", borderRadius: "0 3px 3px 0" }} />

      {/* Screen */}
      <div style={{
        flex: 1, background: WA.white, borderRadius: 38,
        overflow: "hidden", display: "flex", flexDirection: "column", position: "relative",
      }}>
        {/* Dynamic island */}
        <div style={{
          position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)",
          width: 88, height: 26, background: "#1A1A1C",
          borderRadius: 20, zIndex: 10,
        }} />

        {/* Status bar */}
        <div style={{
          height: 44, background: WA.white, flexShrink: 0,
          display: "flex", alignItems: "flex-end", justifyContent: "space-between",
          padding: "0 18px 5px", zIndex: 5,
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: WA.text }}>9:41</span>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <svg width="14" height="10" viewBox="0 0 14 10" fill={WA.text}>
              <rect x="0" y="6" width="2.5" height="4" rx="0.5"/>
              <rect x="3.8" y="4" width="2.5" height="6" rx="0.5"/>
              <rect x="7.6" y="2" width="2.5" height="8" rx="0.5"/>
              <rect x="11.4" y="0" width="2.5" height="10" rx="0.5" opacity="0.3"/>
            </svg>
            <svg width="13" height="10" viewBox="0 0 13 10" fill="none" stroke={WA.text} strokeWidth="1.3" strokeLinecap="round">
              <path d="M0.5 3.5c1.6-1.6 3.7-2.5 6-2.5s4.4.9 6 2.5"/>
              <path d="M2.5 6c1-1 2.4-1.6 4-1.6s3 .6 4 1.6"/>
              <path d="M4.8 8.3c.6-.6 1.4-.9 1.8-.9s1.2.3 1.8.9"/>
              <circle cx="6.6" cy="10" r="0.7" fill={WA.text} stroke="none"/>
            </svg>
            <div style={{ display: "flex", alignItems: "center", gap: 1 }}>
              <div style={{ width: 20, height: 10, border: `1.5px solid ${WA.text}`, borderRadius: 3, padding: 1.5, display: "flex" }}>
                <div style={{ flex: 0.82, background: WA.text, borderRadius: 1 }} />
              </div>
              <div style={{ width: 2, height: 4, background: WA.text, borderRadius: "0 1px 1px 0" }} />
            </div>
          </div>
        </div>

        {/* WhatsApp flow header */}
        <div style={{
          background: `linear-gradient(135deg, ${WA.tealDark} 0%, ${WA.teal} 100%)`,
          padding: "9px 14px 11px",
          display: "flex", alignItems: "center", gap: 8,
          flexShrink: 0,
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        }}>
          <button onClick={handleBack} disabled={!canGoBack && !submitted}
            style={{
              background: "none", border: "none", cursor: canGoBack || submitted ? "pointer" : "default",
              padding: 2, color: WA.white, fontSize: 14, display: "flex", alignItems: "center",
              opacity: canGoBack || submitted ? 1 : 0.4,
            }}>
            <LeftOutlined />
          </button>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ color: WA.white, fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {flow?.name || "Form"}
            </div>
            {!submitted && screens.length > 1 && (
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 10.5, marginTop: 1 }}>
                Step {screenIdx + 1} of {screens.length}
              </div>
            )}
          </div>
          <CloseOutlined style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }} />
        </div>

        {/* Progress bar */}
        {!submitted && screens.length > 1 && (
          <div style={{ height: 2.5, background: "rgba(0,0,0,0.08)", flexShrink: 0 }}>
            <div style={{
              height: "100%",
              width: `${((screenIdx + 1) / screens.length) * 100}%`,
              background: WA.green,
              transition: "width 0.3s ease",
            }} />
          </div>
        )}

        {/* Content */}
        {submitted ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, background: WA.white, textAlign: "center" }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>🎉</div>
            <CheckCircleFilled style={{ fontSize: 0, color: WA.green, marginBottom: 0 }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: WA.text, marginBottom: 8 }}>Submitted!</div>
            <div style={{ fontSize: 12, color: WA.subtext, lineHeight: 1.5 }}>Your response has been received.</div>
          </div>
        ) : screen ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#FAFAFA" }}>
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 14px 4px", background: WA.white }}>
              {screen.title && (
                <div style={{ fontSize: 17, fontWeight: 700, color: WA.text, marginBottom: 14, paddingBottom: 12, borderBottom: `1.5px solid ${WA.border}` }}>
                  {screen.title}
                </div>
              )}
              {bodyComps.length === 0 && (
                <div style={{ color: WA.label, fontSize: 12, fontStyle: "italic", textAlign: "center", marginTop: 40, lineHeight: 1.6 }}>
                  Add components to this screen
                </div>
              )}
              {bodyComps.map((c, i) => <Comp key={c.id || i} comp={c} onAction={handleAction} />)}
            </div>
            {footerComp ? (
              <Comp comp={footerComp} onAction={handleAction} />
            ) : (
              <div style={{ padding: "10px 14px 14px", borderTop: `1px solid ${WA.border}`, background: WA.white }}>
                <button style={{
                  width: "100%", padding: 13, borderRadius: 12, border: "none",
                  background: "#D1D5DB", color: WA.label,
                  fontSize: 14, fontWeight: 600, cursor: "default",
                }}>
                  Continue
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: WA.label, fontSize: 13 }}>
            No screens yet
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Main export ─────────────────────────────────────────── */
export default function FlowPreview({ flow }) {
  const screens = flow?.screens || [];
  const [screenIdx, setScreenIdx] = useState(0);
  const [history, setHistory]     = useState([]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setScreenIdx(0);
    setHistory([]);
    setSubmitted(false);
  }, [flow?.screens?.length, flow?.screens?.[0]?.id]);

  const reset = () => { setScreenIdx(0); setHistory([]); setSubmitted(false); };

  return (
    <div style={{
      display: "flex",
      height: "100%",
      background: "linear-gradient(135deg, #0D3B2E 0%, #1A5C47 40%, #0D3B2E 100%)",
      borderRadius: 10,
      overflow: "hidden",
      position: "relative",
    }}>
      {/* Subtle grid overlay */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }} />

      {/* Left panel */}
      <div style={{
        width: 200,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        padding: "28px 16px 20px",
        zIndex: 1,
        borderRight: "1px solid rgba(255,255,255,0.07)",
        background: "rgba(0,0,0,0.15)",
      }}>
        {/* Flow info */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>Flow</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", lineHeight: 1.3, wordBreak: "break-word" }}>
            {flow?.name || "Untitled"}
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 4 }}>
            {screens.length} screen{screens.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Screen list */}
        {screens.length > 0 && (
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>Screens</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, overflowY: "auto", maxHeight: "calc(100% - 28px)" }}>
              {screens.map((s, i) => {
                const active = !submitted && screenIdx === i;
                return (
                  <button key={s.id || i} onClick={() => { setSubmitted(false); setScreenIdx(i); setHistory([]); }} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer", textAlign: "left",
                    background: active ? "rgba(37,211,102,0.18)" : "rgba(255,255,255,0.05)",
                    transition: "background 0.15s",
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      background: active ? WA.green : "rgba(255,255,255,0.12)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 10, fontWeight: 700, color: "#fff",
                    }}>
                      {i + 1}
                    </div>
                    <span style={{
                      fontSize: 12, fontWeight: active ? 600 : 400,
                      color: active ? "#fff" : "rgba(255,255,255,0.6)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {s.title || s.id || `Screen ${i + 1}`}
                    </span>
                    {s.terminal && (
                      <span style={{ fontSize: 9, color: WA.green, fontWeight: 700, marginLeft: "auto", flexShrink: 0 }}>END</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Reset */}
        <button onClick={reset} style={{
          marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)",
          background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.65)",
          fontSize: 12, fontWeight: 500, cursor: "pointer",
        }}>
          <ReloadOutlined style={{ fontSize: 11 }} />
          Restart
        </button>
      </div>

      {/* Right: phone */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, padding: 16 }}>
        {/* Glow behind phone */}
        <div style={{
          position: "absolute", width: 200, height: 200,
          background: "radial-gradient(circle, rgba(37,211,102,0.15) 0%, transparent 70%)",
          borderRadius: "50%", pointerEvents: "none",
        }} />
        <Phone
          flow={flow}
          screens={screens}
          screenIdx={screenIdx}
          setScreenIdx={setScreenIdx}
          history={history}
          setHistory={setHistory}
          submitted={submitted}
          setSubmitted={setSubmitted}
        />
      </div>
    </div>
  );
}
